/**
 * Admin → Doublons modal
 *
 * Opens from the Clients tab. Fetches /api/admin/clients/duplicates then
 * renders each group with a side-by-side comparison. The user picks the
 * "keeper" account (the one that will survive), optionally edits the final
 * name/phone/plate list, and clicks "Fusionner".
 *
 * On merge success, callback onMerged() refreshes the parent list.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Phone, Car, User, Users, Loader2, X, GitMerge, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_LABEL = {
  phone: { label: "Même téléphone", icon: Phone, color: "text-amber-400" },
  plate: { label: "Même plaque",     icon: Car,   color: "text-blue-400" },
  name:  { label: "Noms similaires", icon: User,  color: "text-purple-400" },
};

const normPlates = (raw) =>
  (raw || "")
    .split(/[,\s]+/)
    .map((p) => p.trim().toUpperCase())
    .filter(Boolean);

// Compact stat tile for the merge-preview grid.
const PreviewStat = ({ label, value, highlight = false }) => (
  <div
    className={`px-3 py-2 rounded border ${
      highlight && value > 0
        ? "bg-[#FF3B30]/10 border-[#FF3B30]/30"
        : "bg-white/[0.02] border-white/[0.06]"
    }`}
  >
    <div className={`text-lg font-black italic ${
      value > 0 ? (highlight ? "text-[#FF3B30]" : "text-white") : "text-white/40"
    }`}>
      {value}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
  </div>
);

export default function DuplicatesModal({ open, onClose, onMerged, focusPhone = null }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  // Per-group state keyed by group index: { keeper, name, phone, plates, preview }
  const [choices, setChoices] = useState({});
  const [mergingIdx, setMergingIdx] = useState(null);

  // Fetch impact preview for a given group when the user picks a keeper.
  // Debounced via a per-group inflight ref so rapid radio switches don't thrash.
  const previewFor = useCallback(async (idx, keeper, allEmails) => {
    const duplicates = allEmails.filter((e) => e !== keeper);
    if (duplicates.length === 0) return;
    try {
      const r = await axios.post(`${API}/api/admin/clients/merge/preview`, {
        keeper_email: keeper,
        duplicate_emails: duplicates,
      });
      setChoices((p) => ({ ...p, [idx]: { ...p[idx], preview: r.data } }));
    } catch {
      // Non-blocking — preview is informational.
    }
  }, []);

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/clients/duplicates`);
      const all = r.data.groups || [];
      // When opened from the post-save nudge, show only the matching phone group.
      const focused = focusPhone ? all.filter((x) => x.reason === "phone" && x.signal === focusPhone) : [];
      const g = focused.length ? focused : all;
      setGroups(g);
      // Default keeper = the client with most appointments (ties → first in list)
      const init = {};
      g.forEach((group, idx) => {
        const best = [...group.clients].sort((a, b) => (b.total_appointments || 0) - (a.total_appointments || 0))[0];
        const mergedPlates = Array.from(new Set(group.clients.flatMap((c) => c.plates || []))).join(", ");
        init[idx] = {
          keeper: best?.client_email,
          name: best?.client_name || "",
          phone: best?.client_phone || "",
          plates: mergedPlates,
        };
      });
      setChoices(init);
      // Kick off impact previews in parallel
      g.forEach((group, idx) => {
        const keeper = init[idx]?.keeper;
        const allEmails = group.clients.map((c) => c.client_email);
        if (keeper) previewFor(idx, keeper, allEmails);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de détection des doublons");
    } finally {
      setLoading(false);
    }
  }, [previewFor, focusPhone]);

  useEffect(() => {
    if (open) fetchDuplicates();
  }, [open, fetchDuplicates]);

  const totalDuplicateClients = useMemo(
    () => groups.reduce((acc, g) => acc + g.clients.length, 0),
    [groups]
  );

  const handleMerge = async (idx) => {
    const group = groups[idx];
    const choice = choices[idx];
    if (!choice?.keeper) return toast.error("Choisissez un compte à conserver");
    const duplicateEmails = group.clients.map((c) => c.client_email).filter((e) => e !== choice.keeper);
    if (duplicateEmails.length === 0) return toast.error("Aucun doublon à fusionner");

    const p = choice.preview?.will_move || {};
    const impactLine = [
      p.appointments ? `${p.appointments} RDV` : null,
      p.quotes       ? `${p.quotes} devis`    : null,
      p.notes        ? `${p.notes} notes`     : null,
      p.messages     ? `${p.messages} messages` : null,
    ].filter(Boolean).join(" · ") || "aucune donnée liée";

    if (!window.confirm(
      `Fusionner ${duplicateEmails.length} fiche(s) dans ${choice.keeper} ?\n\n` +
      `Impact : ${impactLine} seront réassignés.\n\n` +
      "Cette action est irréversible."
    )) return;

    setMergingIdx(idx);
    try {
      const res = await axios.post(`${API}/api/admin/clients/merge`, {
        keeper_email: choice.keeper,
        duplicate_emails: duplicateEmails,
        final_name: choice.name,
        final_phone: choice.phone,
        final_plates: normPlates(choice.plates),
      });
      const r = res.data.reassigned || {};
      toast.success(
        `Fusion réussie — ${res.data.merged_count} fiche(s) consolidée(s)` +
        (r.appointments ? ` · ${r.appointments} RDV` : "")
      );
      await fetchDuplicates();
      if (onMerged) onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur à la fusion");
    } finally {
      setMergingIdx(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-2 md:p-6 bg-black/80 backdrop-blur-sm overflow-y-auto"
      data-testid="duplicates-modal"
    >
      <div className="relative w-full max-w-4xl bg-[#0F0F0F] border border-white/[0.08] rounded-xl shadow-2xl my-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 md:p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#FF3B30]/15 flex items-center justify-center flex-shrink-0">
              <GitMerge size={16} className="text-[#FF3B30]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-white font-bold uppercase italic tracking-tight">
                Doublons clients
              </h2>
              <p className="text-white/50 text-xs">
                {loading
                  ? "Analyse en cours…"
                  : groups.length === 0
                  ? "Aucun doublon détecté"
                  : `${groups.length} groupe(s) · ${totalDuplicateClients} fiche(s)`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="duplicates-close"
            className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-white"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 md:p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-12 text-white/40">
              <Loader2 className="animate-spin" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center p-12">
              <CheckCircle2 size={40} className="mx-auto text-emerald-400 mb-3" />
              <p className="text-white/70">Base client propre, aucun doublon détecté 👍</p>
            </div>
          ) : (
            groups.map((group, idx) => {
              const meta = REASON_LABEL[group.reason] || REASON_LABEL.name;
              const Icon = meta.icon;
              const choice = choices[idx] || {};
              return (
                <div
                  key={`${group.reason}-${group.signal}-${idx}`}
                  data-testid={`duplicate-group-${idx}`}
                  className="bg-[#141414] border border-white/[0.06] rounded-lg p-4"
                >
                  {/* Group header */}
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={14} className={meta.color} />
                    <span className={`text-xs font-bold uppercase tracking-wider ${meta.color}`}>
                      {meta.label}
                    </span>
                    <span className="text-white/40 text-xs">•</span>
                    <span className="text-white/60 text-xs font-mono">{group.signal}</span>
                  </div>

                  {/* Clients grid — radio keeper */}
                  <div className="space-y-2 mb-3">
                    {group.clients.map((c) => {
                      const isKeeper = choice.keeper === c.client_email;
                      return (
                        <label
                          key={c.client_email}
                          className={`flex items-start gap-3 p-3 rounded-md cursor-pointer transition-all border ${
                            isKeeper
                              ? "bg-[#FF3B30]/10 border-[#FF3B30]/40"
                              : "bg-[#1C1C1E] border-white/[0.06] hover:border-white/[0.15]"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`keeper-${idx}`}
                            value={c.client_email}
                            checked={isKeeper}
                            onChange={() => {
                              setChoices((p) => ({
                                ...p,
                                [idx]: {
                                  ...p[idx],
                                  keeper: c.client_email,
                                  name: c.client_name || p[idx]?.name || "",
                                  phone: c.client_phone || p[idx]?.phone || "",
                                  preview: null,  // reset preview while new one loads
                                },
                              }));
                              previewFor(idx, c.client_email, group.clients.map((x) => x.client_email));
                            }}
                            data-testid={`duplicate-keeper-${idx}-${c.client_email}`}
                            className="mt-0.5 accent-[#FF3B30]"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-semibold text-sm">{c.client_name || "Sans nom"}</span>
                              {isKeeper && (
                                <span className="text-[10px] uppercase tracking-wider bg-[#FF3B30]/20 text-[#FF3B30] px-1.5 py-0.5 rounded">
                                  à conserver
                                </span>
                              )}
                              <span className="text-[10px] text-white/50 bg-white/5 px-1.5 py-0.5 rounded">
                                {c.total_appointments || 0} RDV
                              </span>
                            </div>
                            <div className="text-white/60 text-xs mt-0.5 truncate">{c.client_email}</div>
                            <div className="text-white/40 text-xs mt-0.5 flex items-center gap-3 flex-wrap">
                              <span>{c.client_phone || "—"}</span>
                              <span className="text-[#FF3B30] font-mono">{(c.plates || []).join(", ") || "—"}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Final values editor */}
                  <div className="grid md:grid-cols-3 gap-2 mb-3">
                    <input
                      value={choice.name || ""}
                      onChange={(e) => setChoices((p) => ({ ...p, [idx]: { ...p[idx], name: e.target.value } }))}
                      placeholder="Nom final"
                      data-testid={`duplicate-final-name-${idx}`}
                      autoCapitalize="words"
                      className="bg-[#1C1C1E] border border-white/[0.06] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                    />
                    <input
                      value={choice.phone || ""}
                      onChange={(e) => setChoices((p) => ({ ...p, [idx]: { ...p[idx], phone: e.target.value } }))}
                      placeholder="Téléphone final"
                      data-testid={`duplicate-final-phone-${idx}`}
                      className="bg-[#1C1C1E] border border-white/[0.06] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                    />
                    <input
                      value={choice.plates || ""}
                      onChange={(e) => setChoices((p) => ({ ...p, [idx]: { ...p[idx], plates: e.target.value.toUpperCase() } }))}
                      placeholder="Plaques (séparées par virgule)"
                      data-testid={`duplicate-final-plates-${idx}`}
                      style={{ textTransform: "uppercase" }}
                      className="bg-[#1C1C1E] border border-white/[0.06] rounded px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-white/20"
                    />
                  </div>

                  {/* Impact preview — shows what will move on merge */}
                  {choice.preview && (
                    <div
                      data-testid={`duplicate-preview-${idx}`}
                      className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-center"
                    >
                      <PreviewStat label="RDV" value={choice.preview.will_move?.appointments || 0} highlight />
                      <PreviewStat label="Devis" value={choice.preview.will_move?.quotes || 0} />
                      <PreviewStat label="Notes" value={choice.preview.will_move?.notes || 0} />
                      <PreviewStat label="Messages" value={choice.preview.will_move?.messages || 0} />
                    </div>
                  )}

                  <div className="flex items-start gap-2 text-white/40 text-xs mb-3">
                    <AlertTriangle size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
                    Les éléments ci-dessus seront réassignés au compte « à conserver ». Action irréversible.
                  </div>

                  <button
                    onClick={() => handleMerge(idx)}
                    disabled={mergingIdx === idx || !choice.keeper}
                    data-testid={`duplicate-merge-btn-${idx}`}
                    className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-white/10 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider px-4 py-2.5 rounded"
                  >
                    {mergingIdx === idx ? <Loader2 className="animate-spin" size={14} /> : <GitMerge size={14} />}
                    Fusionner ce groupe
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// Helper exported for ClientsTab's "Doublon ?" badge logic.
export function clientHasPotentialDuplicate(client, allClients) {
  if (!client || !allClients?.length) return false;
  const normalizePhone = (raw) => {
    if (!raw) return "";
    let d = raw.replace(/[^\d+]/g, "");
    if (d.startsWith("+33")) d = "0" + d.slice(3);
    else if (d.startsWith("0033")) d = "0" + d.slice(4);
    return d.replace(/[^\d]/g, "");
  };
  const stripAccents = (s) =>
    (s || "").normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase().trim();

  const myPhone = normalizePhone(client.client_phone);
  const myPlates = new Set(client.plates || []);
  const myName = stripAccents(client.client_name);

  for (const other of allClients) {
    if (other.client_email === client.client_email) continue;
    // Phone
    if (myPhone && myPhone.length >= 6 && normalizePhone(other.client_phone) === myPhone) return true;
    // Plate
    for (const p of other.plates || []) {
      if (myPlates.has(p)) return true;
    }
    // Name similarity — ratcheted Jaccard over trigrams, cheap and good enough.
    const otherName = stripAccents(other.client_name);
    if (myName && otherName && Math.abs(myName.length - otherName.length) <= Math.max(myName.length, otherName.length) * 0.4) {
      // Quick substring heuristic that catches "jean dupon" vs "jean dupont"
      if (otherName.includes(myName) || myName.includes(otherName)) return true;
      // Trigram overlap
      const trig = (s) => {
        const padded = `  ${s}  `;
        const out = new Set();
        for (let i = 0; i < padded.length - 2; i++) out.add(padded.slice(i, i + 3));
        return out;
      };
      const a = trig(myName), b = trig(otherName);
      let inter = 0;
      a.forEach((t) => b.has(t) && inter++);
      const union = a.size + b.size - inter;
      if (union > 0 && inter / union >= 0.6) return true;
    }
  }
  return false;
}
